import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ExternalLink from './Svg/ExternalLinkIcon';

import { isPortrait } from '../utils/size';
import { copyFor } from '../copy/copy';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
    padding: 12px 0;
`;

const Textarea = styled.textarea`
    height: 70px;
    border-radius: 2px;
    border: solid 1px
        ${props =>
            !props.default || props.active || props.valid ? '#f1f1ee' : '#ff245e'};
    color: #333;
    font-family: Averta;
    font-size: 12px;
    line-height: 16px;
    padding: 8px;
    resize: none;
    -webkit-appearance: none;

    @media (max-width: 30em) {
        height: 205px;
    }

    @media (max-width: 30em) {
        margin-left: 0;
        margin-right: 0;
    }

    @media (min-width: 30em) {
        &.message-response {
            position: absolute;
            bottom: -120px;
            width: 100%;
            transition: height 0.6s;
        }
    }

    &:focus,
    &:active {
        outline: none;
        border: solid 1px #979191;
    }
`;

const LabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Label = styled.label`
    border-radius: 5px;
    font-family: Averta;
    left: 5px;
    letter-spacing: 1px;
    line-height: 1.5;
    margin-bottom: 0.5em;
    padding: 0 5px;
    position: absolute;
    top: ${props =>
        props.default
            ? isPortrait() ? `9px` : `0px !important;` //eslint-disable-line
            : isPortrait() ? `-6px` : `-12px` //eslint-disable-line
    };
    ${props => props.class === 'volunteer' && `top: -6px`};
    background-color: ${props =>
        props.labelBackgroundColor ? props.labelBackgroundColor : `white`};
    color: ${props =>
        props.default || props.active || props.valid ? `#979191` : `#ff245e`};
    font-size: ${props => (props.default ? 14 : 8)}px;
    font-weight: ${props => (props.default ? `400` : `600`)};
    text-transform: ${props => (props.default ? `capitalize` : `uppercase`)};
    transition: all 250ms ease;
    z-index: 2;
`;

const ExpandButton = styled.div`
    position: absolute;
    top: 185px;
    right: 25px;
    font-size: 12px;
    color: #fff;
    line-height: 1.2;
    background: #072647;
    border-radius: 3px;
    opacity: 0.5;
    padding: 4px 6px;
    transition: opacity 0.2s, top 0.2s;

    @media (max-width: 30em) {
        top: 310px;
        right: 20px;
    }

    &:hover {
        opacity: 0.6;
        cursor: pointer;
    }

    #exitLabel {
        display: none;
        padding-left: 16px;
    }
`;

const CloseButton = styled.span`
    width: 14px;
    height: 26px;
    transform: rotate(45deg);
    font-family: Averta;
    font-size: 24px;
    line-height: 1.08;
    text-align: left;
    color: #fff;
    top: -2px;
    left: 5px;
    position: absolute;
    cursor: pointer;
    display: inline-block;
`;

const expandTextarea = () => {
    var textarea = document.getElementById('textarea');
    textarea.classList.toggle('expanded');

    var expandButton = document.getElementById('expandButton');
    expandButton.classList.toggle('expanded');

    var expandLabel = document.getElementById('expandLabel');
    var exitLabel = document.getElementById('exitLabel');
    expandLabel.style.display = expandLabel.style.display === 'none' ? 'block' : 'none';
    exitLabel.style.display = exitLabel.style.display === 'block' ? 'none' : 'block';

    document.querySelectorAll('.row-wrapper-1')[0].classList.toggle('focused');
    document.querySelectorAll('.row-wrapper-2')[0].classList.toggle('focused');
};

class TextareaGroup extends React.Component {
    render() {
        const defaultState =
            !this.props.meta.active &&
            !this.props.meta.valid &&
            (this.props.meta.pristine || this.props.input.value === '');
        const selected = this.props.meta.active;
        const error =
            this.props.meta.invalid &&
            this.props.meta.touched &&
            this.props.input.value !== '';

        return (
            <Container>
                <LabelContainer className={this.props.classLabel} id="labelResponse">
                    <Label
                        {...this.props.input}
                        {...this.props.meta}
                        readOnly={this.props.readOnly}
                        class={this.props.class}
                        default={defaultState}
                        labelBackgroundColor={this.props.labelBackgroundColor}
                    >
                        {error && !selected ? this.props.meta.error : this.props.label}
                    </Label>
                </LabelContainer>
                <Textarea
                    {...this.props.input}
                    {...this.props.meta}
                    textareaColor={this.props.textareaColor}
                    maxLength={this.props.maxLength}
                    placeholder={this.props.placeholder}
                    autocomplete={this.props.autocomplete}
                    name={this.props.title}
                    onFocus={() => {
                        if (this.props.toggleTextAreaFocused) {
                            this.props.toggleTextAreaFocused();
                        }
                    }}
                    onBlur={() =>
                        setTimeout(() => {
                            if (this.props.toggleTextAreaFocused) {
                                this.props.toggleTextAreaFocused();
                            }
                        }, 30)
                    }
                    id="textarea"
                    className={this.props.class}
                    readOnly={this.props.readOnly}
                />
                {this.props.expandButton && (
                    <ExpandButton onClick={expandTextarea} id="expandButton">
                        <span id="expandLabel">
                            <ExternalLink /> {copyFor('expandableTextArea', 'expand')}
                        </span>
                        <span id="exitLabel">
                            <CloseButton>+</CloseButton>
                            {copyFor('expandableTextArea', 'exit')}
                        </span>
                    </ExpandButton>
                )}
            </Container>
        );
    }
}

TextareaGroup.propTypes = {
    label: PropTypes.string.isRequired,
    labelBackgroundColor: PropTypes.string,
    meta: PropTypes.object.isRequired,
    primary: PropTypes.bool,
    autoFocus: PropTypes.bool,
    maxLength: PropTypes.number,
    autoFocusTimeout: PropTypes.number,
    placeholder: PropTypes.string,
    autocomplete: PropTypes.string,
    title: PropTypes.string,
    class: PropTypes.string,
    classLabel: PropTypes.string,
    textareaColor: PropTypes.string,
    toggleTextAreaFocused: PropTypes.func,
    readOnly: PropTypes.bool,
    expandButton: PropTypes.bool,
    input: PropTypes.object
};

export default TextareaGroup;
